import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { 
  getCurrentUser,
  getUserData,
  logoutUser,
  loginUser,
  registerUser,
  type RegisterData
} from '../lib/firebase/auth';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../lib/firebase/config';
import type { User as FirebaseUser, UserCredential } from 'firebase/auth';
import type { User } from '../types'; // Importer User depuis types

// Créer une interface AuthUser qui étend User
export interface AuthUser extends User {
  // Champs supplémentaires si nécessaire
  firebaseUser?: FirebaseUser;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<UserCredential>;
  register: (
    email: string, 
    password: string, 
    displayName: string, 
    role: 'supervisor' | 'secretary', 
    institutionId?: string,
    additionalData?: {
      firstName?: string;
      lastName?: string;
      phone?: string;
    }
  ) => Promise<UserCredential>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (data: Partial<AuthUser>) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  login: async () => { throw new Error('Function not implemented'); },
  register: async () => { throw new Error('Function not implemented'); },
  logout: async () => {},
  refreshUser: async () => {},
  resetPassword: async () => {},
  updateUserProfile: async () => {},
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    setLoading(true);
    try {
      const firebaseUser = getCurrentUser();
      if (firebaseUser) {
        const userData = await getUserData(firebaseUser.uid);
        // Créer un objet AuthUser en combinant les données
        const authUser: AuthUser = {
          ...userData,
          firebaseUser
        };
        setUser(authUser);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<UserCredential> => {
    setLoading(true);
    try {
      const userCredential = await loginUser(email, password);
      await refreshUser();
      return userCredential;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (
    email: string, 
    password: string, 
    displayName: string, 
    role: 'supervisor' | 'secretary', 
    institutionId?: string,
    additionalData?: {
      firstName?: string;
      lastName?: string;
      phone?: string;
    }
  ): Promise<UserCredential> => {
    setLoading(true);
    try {
      const registerData: RegisterData = {
        email,
        password,
        displayName,
        role,
        institutionId,
        firstName: additionalData?.firstName,
        lastName: additionalData?.lastName,
        phone: additionalData?.phone
      };

      const userCredential = await registerUser(registerData);
      await refreshUser();
      return userCredential;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateUserProfile = async (data: Partial<AuthUser>) => {
    if (!user) {
      throw new Error('No user logged in');
    }
    
    try {
      // Retirer firebaseUser des données avant l'envoi à Firestore
      const { firebaseUser, ...userData } = data;
      const { updateUserProfile: updateProfileInFirestore } = await import('../lib/firebase/auth');
      await updateProfileInFirestore(user.uid, userData);
      await refreshUser();
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await logoutUser();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    setLoading(true);
    try {
      const { resetPassword: resetPasswordFunction } = await import('../lib/firebase/auth');
      await resetPasswordFunction(email);
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        try {
          const userData = await getUserData(firebaseUser.uid);
          // Créer un objet AuthUser
          const authUser: AuthUser = {
            ...userData,
            firebaseUser
          };
          setUser(authUser);
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const value: AuthContextType = {
    user, 
    loading, 
    login, 
    register, 
    logout, 
    refreshUser,
    resetPassword,
    updateUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};